import { useState } from 'react';
import {
  makeStyles, tokens, Title2, Title3, Body1, Caption1, Spinner, Badge,
  MessageBar, MessageBarBody,
} from '@fluentui/react-components';
import { ProjectPicker } from '../../../components/ProjectPicker';
import { useCustomerRoadmap } from '../../../api/hooks';
import type { CustomerRoadmapEpic, CustomerRoadmapQuarter } from '../../../api/types';

// FEAT-024 Customer Roadmap. Quarterly roadmap, strict customer-language. The API has
// already filtered out internal-only items + run LeakGuard.AssertSafe, so anything we
// render here is customer-safe by construction.
const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, height: '100%' },
  hint: { color: tokens.colorNeutralForeground2 },
  toolbar: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  quarter: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  quarterHeader: { display: 'flex', alignItems: 'baseline', gap: tokens.spacingHorizontalM },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(20rem, 1fr))',
    gap: tokens.spacingHorizontalL,
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingHorizontalL,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  cardHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: tokens.spacingHorizontalM },
  fallbackBanner: { color: tokens.colorPaletteYellowForeground1 },
});

function statusColor(s: CustomerRoadmapEpic['status']) {
  switch (s) {
    case 'Delivered': return 'success' as const;
    case 'InProgress': return 'brand' as const;
    case 'Planned': return 'subtle' as const;
    default: return 'informative' as const;
  }
}

function statusLabel(s: CustomerRoadmapEpic['status']): string {
  return s === 'InProgress' ? 'In Progress' : s;
}

export function CustomerRoadmapPage() {
  const styles = useStyles();
  const [projectId, setProjectId] = useState<string | undefined>();
  const { data, isLoading, error } = useCustomerRoadmap(projectId);

  const hasFallbacks = data?.quarters.some((q: CustomerRoadmapQuarter) => q.epics.some((e: CustomerRoadmapEpic) => e.usingFallback));

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <Title2>Your Roadmap</Title2>
        <Body1 className={styles.hint}>
          Every initiative on your project mapped to a quarter — what's coming, what's in progress, what's already delivered.
        </Body1>
        <ProjectPicker selectedProjectId={projectId} onSelect={setProjectId} />
      </div>

      {!projectId ? (
        <Body1>Pick a project to see its roadmap.</Body1>
      ) : isLoading ? (
        <Spinner label="Loading roadmap…" />
      ) : error ? (
        <MessageBar intent="error"><MessageBarBody>{(error as Error).message}</MessageBarBody></MessageBar>
      ) : !data || data.quarters.length === 0 ? (
        <Body1>Nothing is on the roadmap yet for this project.</Body1>
      ) : (
        <>
          {hasFallbacks && (
            <MessageBar intent="info">
              <MessageBarBody>
                <span className={styles.fallbackBanner}>
                  Some items still use engineering titles — your delivery team will replace these with business-language titles soon.
                </span>
              </MessageBarBody>
            </MessageBar>
          )}
          {data.quarters.map((q: CustomerRoadmapQuarter) => (
            <div key={q.quarter} className={styles.quarter}>
              <div className={styles.quarterHeader}>
                <Title3>{q.quarter}</Title3>
                <Caption1>{q.epics.length} initiative{q.epics.length === 1 ? '' : 's'}</Caption1>
              </div>
              <div className={styles.grid}>
                {q.epics.map((e: CustomerRoadmapEpic) => (
                  <div key={`${q.quarter}-${e.customerTitle}`} className={styles.card}>
                    <div className={styles.cardHeader}>
                      <Title3>{e.customerTitle}</Title3>
                      <Badge appearance="filled" color={statusColor(e.status)}>{statusLabel(e.status)}</Badge>
                    </div>
                    {e.customerDescription ? <Body1>{e.customerDescription}</Body1> : null}
                    <Caption1>Updated {new Date(e.lastUpdated).toLocaleDateString()}</Caption1>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
